import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

/**
 * Signal Administration (Izba Admin → Config). Every API call the admin
 * screens make goes through these thunks; the screens dispatch them and
 * keep their own view state. Rejections carry a readable message string.
 */

/** The API's error message (ApiRequest throws an Error whose message is
 * the JSON error body), else `fallback`. */
const apiError = (error: unknown, fallback: string): string => {
  if (!(error instanceof Error) || !error.message) return fallback;
  try {
    const parsed = JSON.parse(error.message) as {
      message?: string;
      detail?: string;
    };
    return parsed?.message || parsed?.detail || fallback;
  } catch {
    return error.message || fallback;
  }
};

const content = (res: unknown): any =>
  (res as { content?: unknown })?.content ?? res;

export const fetchSignalAdminOrganizations = createAsyncThunk(
  "signalAdmin/organizations",
  async (_: void, { rejectWithValue }) => {
    try {
      return content(await get(API_URLS.fetchSignalAdminOrganizations()))
        ?.organizations;
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't load organizations."));
    }
  }
);

/** Platform config, its versions, and the registry — the org's (with live
 * counts) when one is chosen, else the platform's own. */
export const fetchSignalAdminPlatformPolicy = createAsyncThunk(
  "signalAdmin/platformPolicy",
  async (orgId: string | undefined, { rejectWithValue }) => {
    try {
      const [config, audit, registry] = await Promise.all([
        get(API_URLS.signalAdminPlatformConfig()),
        get(API_URLS.fetchSignalAdminPlatformAudit()),
        get(
          orgId
            ? API_URLS.fetchSignalAdminRegistry(orgId)
            : API_URLS.fetchSignalAdminPlatformRegistry()
        ),
      ]);
      return {
        config: content(config),
        versions: content(audit)?.versions ?? [],
        registry: content(registry),
      };
    } catch (e) {
      return rejectWithValue(
        apiError(e, "Couldn't load platform signal settings.")
      );
    }
  }
);

export const fetchSignalAdminOrgInspection = createAsyncThunk(
  "signalAdmin/orgInspection",
  async (orgId: string, { rejectWithValue }) => {
    try {
      const [overview, registry, presentation] = await Promise.all([
        get(API_URLS.fetchSignalAdminOverview(orgId)),
        get(API_URLS.fetchSignalAdminRegistry(orgId)),
        get(API_URLS.signalAdminOrgPresentation(orgId)),
      ]);
      return {
        overview: content(overview),
        registry: content(registry),
        presentation: content(presentation),
      };
    } catch (e) {
      return rejectWithValue(
        apiError(e, "Couldn't load organization inspection data.")
      );
    }
  }
);

export const previewSignalAdminPlatformConfig = createAsyncThunk(
  "signalAdmin/previewPlatformConfig",
  async (payload: Record<string, unknown>, { rejectWithValue }) => {
    try {
      return content(
        await post(API_URLS.previewSignalAdminPlatformConfig(), payload)
      )?.changes;
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't validate this draft."));
    }
  }
);

export const applySignalAdminPlatformConfig = createAsyncThunk(
  "signalAdmin/applyPlatformConfig",
  async (
    { payload, reason }: { payload: Record<string, unknown>; reason: string },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.signalAdminPlatformConfig(), {
          ...payload,
          reason,
        })
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't apply this change."));
    }
  }
);

/** Save "changes requested" action-plan reviews ({definition_id: review})
 * for every admin to see. They write one platform version but change
 * nothing users see, so they need no Apply. */
export const saveSignalAdminActionPlanReviews = createAsyncThunk(
  "signalAdmin/saveActionPlanReviews",
  async (
    reviews: Record<
      string,
      {
        steps: {
          title: string;
          owner: string;
          target: string;
          detail: string;
        }[];
        rationale: string;
        note: string;
        awareness: boolean;
      }
    >,
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.saveSignalAdminActionPlanReviews(), { reviews })
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't save this review."));
    }
  }
);

export const previewSignalAdminRestore = createAsyncThunk(
  "signalAdmin/previewRestore",
  async (version: number, { rejectWithValue }) => {
    try {
      return content(await get(API_URLS.previewSignalAdminRestore(version)))
        ?.changes;
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't compare that version."));
    }
  }
);

export const restoreSignalAdminVersion = createAsyncThunk(
  "signalAdmin/restoreVersion",
  async (
    { version, reason }: { version: number; reason: string },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.restoreSignalAdminVersion(), { version, reason })
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't restore that version."));
    }
  }
);

export const previewSignalAdminOrgPresentation = createAsyncThunk(
  "signalAdmin/previewOrgPresentation",
  async (
    { orgId, payload }: { orgId: string; payload: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.previewSignalAdminOrgPresentation(orgId), payload)
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't validate this change."));
    }
  }
);

export const applySignalAdminOrgPresentation = createAsyncThunk(
  "signalAdmin/applyOrgPresentation",
  async (
    { orgId, payload }: { orgId: string; payload: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.signalAdminOrgPresentation(orgId), payload)
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't apply this change."));
    }
  }
);

/** Drop an organization's override so it follows the platform again. */
export const clearSignalAdminOrgPresentation = createAsyncThunk(
  "signalAdmin/clearOrgPresentation",
  async (
    { orgId, reason }: { orgId: string; reason: string },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.clearSignalAdminOrgPresentation(orgId), { reason })
      );
    } catch (e) {
      return rejectWithValue(
        apiError(e, "Couldn't switch to platform defaults.")
      );
    }
  }
);

export const fetchSignalAdminTraces = createAsyncThunk(
  "signalAdmin/traces",
  async (
    { orgId, params }: { orgId: string; params?: Record<string, string> },
    { rejectWithValue }
  ) => {
    try {
      return content(await get(API_URLS.fetchSignalAdminTraces(orgId, params)));
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't load evaluation traces."));
    }
  }
);

/** Signal pipeline health for every organization (Run Diff badge). */
/** Sweep intervals in force and the sweeper's last/next run. */
export const fetchSignalPipelineStatus = createAsyncThunk(
  "signalAdmin/pipelineStatus",
  async (_: void, { rejectWithValue }) => {
    try {
      return content(await get(API_URLS.fetchSignalPipelineStatus()));
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't load the sweeper status."));
    }
  }
);

/** Run a signal sweep now instead of waiting for the next one. */
export const runSignalSweep = createAsyncThunk(
  "signalAdmin/runSweep",
  async (_: void, { rejectWithValue }) => {
    try {
      return content(await post(API_URLS.runSignalSweep(), {}));
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't start a sweep."));
    }
  }
);

export const fetchSignalPipelineHealth = createAsyncThunk(
  "signalAdmin/pipelineHealth",
  async (_: void, { rejectWithValue }) => {
    try {
      return content(await get(API_URLS.fetchSignalPipelineHealth()));
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't check pipeline health."));
    }
  }
);

/** Rule Classifier: per rule, accounts touched and cards hidden/merged in
 * one organization today, with the admin's unapplied changes on top. */
export const fetchClassifierImpact = createAsyncThunk(
  "signalAdmin/classifierImpact",
  async (
    {
      orgId,
      classifier,
    }: { orgId: string; classifier: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.signalAdminClassifierImpact(orgId), { classifier })
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't work out rule impact."));
    }
  }
);

/** Rule Classifier: one account's cards now and under the draft rules. */
export const tryClassifierOnAccount = createAsyncThunk(
  "signalAdmin/classifierTry",
  async (
    {
      orgId,
      accountId,
      classifier,
    }: {
      orgId: string;
      accountId: string;
      classifier: Record<string, unknown>;
    },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await post(API_URLS.signalAdminClassifierTry(orgId), {
          account_id: accountId,
          classifier,
        })
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't preview this account."));
    }
  }
);

/** Run Diff: recent sweep cycles for one organization. */
export const fetchSignalAdminRuns = createAsyncThunk(
  "signalAdmin/runs",
  async (orgId: string, { rejectWithValue }) => {
    try {
      return content(await get(API_URLS.fetchSignalAdminRuns(orgId)));
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't load sweep runs."));
    }
  }
);

/** Run Diff: what changed between two sweeps (default: back-to-back). */
export const fetchSignalAdminRunDiff = createAsyncThunk(
  "signalAdmin/runDiff",
  async (
    { orgId, params }: { orgId: string; params?: Record<string, string> },
    { rejectWithValue }
  ) => {
    try {
      return content(
        await get(API_URLS.fetchSignalAdminRunDiff(orgId, params))
      );
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't compare these runs."));
    }
  }
);

export const fetchSignalAdminActionHistory = createAsyncThunk(
  "signalAdmin/actionHistory",
  async (orgId: string, { rejectWithValue }) => {
    try {
      return content(await get(API_URLS.fetchSignalAdminActionHistory(orgId)));
    } catch (e) {
      return rejectWithValue(apiError(e, "Couldn't load action history."));
    }
  }
);

interface SignalAdminState {
  /** Version most recently applied from this browser, for the toast. */
  lastAppliedVersion: number | null;
}

const initialState: SignalAdminState = { lastAppliedVersion: null };

const signalAdminSlice = createSlice({
  name: "signalAdmin",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(applySignalAdminPlatformConfig.fulfilled, (state, action) => {
        state.lastAppliedVersion = action.payload?.applied?.version ?? null;
      })
      .addCase(restoreSignalAdminVersion.fulfilled, (state, action) => {
        state.lastAppliedVersion = action.payload?.applied?.version ?? null;
      });
  },
});

export default signalAdminSlice.reducer;
